/**
 * useImmersiveTheme Hook
 * 
 * Hook principal para acessar e manipular o sistema de temas imersivos.
 * Re-exporta o hook do context com funcionalidades adicionais.
 */

import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import {
  ImmersiveThemeType,
  ThemePreset,
  ThemeConfig,
  ImmersiveThemeResponse,
  AllThemePresetsResponse,
  THEME_METADATA,
} from '../types/immersive-theme.types';

// Re-export from context
export {
  useImmersiveTheme,
  useIsThemeActive,
  useThemeClasses,
  useThemeCssVar,
  ImmersiveThemeProvider,
} from '../contexts/ImmersiveThemeContext';

// ============================================
// ADDITIONAL HOOKS
// ============================================

/**
 * Hook para buscar todos os presets de tema
 */
export const useThemePresets = () => {
  return useQuery<ThemePreset[]>({
    queryKey: ['immersive-theme-presets'],
    queryFn: async () => {
      const response = await api.get<AllThemePresetsResponse>('/theming/immersive/presets');
      return response.data.presets;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

/**
 * Hook para buscar um preset específico
 */
export const useThemePreset = (type: ImmersiveThemeType) => {
  return useQuery<ThemePreset>({
    queryKey: ['immersive-theme-preset', type],
    queryFn: async () => {
      const response = await api.get<ThemePreset>(`/theming/immersive/presets/${type}`);
      return response.data;
    },
    staleTime: 30 * 60 * 1000,
    enabled: type !== ImmersiveThemeType.NONE,
  });
};

/**
 * Hook para buscar a configuração de tema atual
 */
export const useCurrentTheme = () => {
  return useQuery<ImmersiveThemeResponse>({
    queryKey: ['immersive-theme'],
    queryFn: async () => {
      const response = await api.get<ImmersiveThemeResponse>('/theming/immersive');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook para atualizar o tema
 */
export const useUpdateTheme = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      themeType: ImmersiveThemeType;
      config?: ThemeConfig;
      colorVariant?: string;
      reducedMotion?: boolean;
      highContrast?: boolean;
    }) => {
      const response = await api.put<ImmersiveThemeResponse>('/theming/immersive', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['immersive-theme'], data);
    },
  });
};

/**
 * Hook para preview de tema (sem persistir)
 */
export const usePreviewTheme = () => {
  return useMutation({
    mutationFn: async (data: {
      themeType: ImmersiveThemeType;
      config?: ThemeConfig;
    }) => {
      const response = await api.post<ImmersiveThemeResponse>('/theming/immersive/preview', data);
      return response.data;
    },
  });
};

/**
 * Hook para resetar o tema
 */
export const useResetTheme = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.delete('/theming/immersive');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['immersive-theme'] });
    },
  });
};

/**
 * Hook para obter metadata do tema
 */
export const useThemeMetadata = (type: ImmersiveThemeType) => {
  return useMemo(() => THEME_METADATA[type], [type]);
};

/**
 * Hook para verificar se o tema suporta modo escuro
 */
export const useThemeSupportsDarkMode = (type: ImmersiveThemeType) => {
  const metadata = useThemeMetadata(type);
  return metadata.colorScheme === 'dark' || metadata.colorScheme === 'both';
};

/**
 * Hook para obter as variantes de cor disponíveis para um tema
 */
export const useThemeColorVariants = (type: ImmersiveThemeType) => {
  const { data: preset } = useThemePreset(type);
  return preset?.colorVariants || [];
};

/**
 * Hook para aplicar CSS Variables manualmente
 */
export const useApplyCssVariables = () => {
  return useCallback((variables: Record<string, string>, themeType: ImmersiveThemeType) => {
    const root = document.documentElement;
    
    // Remove previous theme variables
    const prefixes = ['--neu-', '--brut-', '--term-', '--org-', '--lux-', '--animation-', '--transition-'];
    const existingVars = Array.from(root.style).filter(
      prop => prefixes.some(prefix => prop.startsWith(prefix))
    );
    existingVars.forEach(prop => root.style.removeProperty(prop));
    
    // Apply new variables
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    
    // Set data attribute
    root.setAttribute('data-immersive-theme', themeType);
  }, []);
};

/**
 * Hook para limpar CSS Variables
 */
export const useClearCssVariables = () => {
  return useCallback(() => {
    const root = document.documentElement;
    
    const prefixes = ['--neu-', '--brut-', '--term-', '--org-', '--lux-', '--animation-', '--transition-'];
    const existingVars = Array.from(root.style).filter(
      prop => prefixes.some(prefix => prop.startsWith(prefix))
    );
    existingVars.forEach(prop => root.style.removeProperty(prop));
    
    root.setAttribute('data-immersive-theme', 'NONE');
  }, []);
};

/**
 * Hook para detectar preferência de reduced-motion do sistema
 */
export const useSystemReducedMotion = () => {
  const query = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return window.matchMedia('(prefers-reduced-motion: reduce)');
  }, []);

  return query?.matches ?? false;
};

/**
 * Hook para obter classes CSS baseadas no tema atual
 */
export const useThemedClassName = (
  baseClass: string,
  variants?: Partial<Record<ImmersiveThemeType, string>>
) => {
  const { data: theme } = useCurrentTheme();
  const currentType = theme?.themeType || ImmersiveThemeType.NONE;

  return useMemo(() => {
    const themeClass = variants?.[currentType] || '';
    const defaultThemeClass = {
      [ImmersiveThemeType.NONE]: '',
      [ImmersiveThemeType.NEUMORPHISM]: 'themed-neumorphism',
      [ImmersiveThemeType.BRUTALIST]: 'themed-brutalist',
      [ImmersiveThemeType.RETRO_TERMINAL]: 'themed-retro-terminal',
      [ImmersiveThemeType.ORGANIC_NATURE]: 'themed-organic-nature',
      [ImmersiveThemeType.LUXURY_GOLD]: 'themed-luxury-gold',
    }[currentType];

    return [baseClass, themeClass || defaultThemeClass].filter(Boolean).join(' ');
  }, [baseClass, variants, currentType]);
};

// ============================================
// SUPER ADMIN HOOKS
// ============================================

/**
 * Hook para Super Admin buscar tema de um tenant específico
 */
export const useTenantTheme = (tenantId: string) => {
  return useQuery<ImmersiveThemeResponse>({
    queryKey: ['immersive-theme', 'tenant', tenantId],
    queryFn: async () => {
      const response = await api.get<ImmersiveThemeResponse>(`/theming/immersive/tenant/${tenantId}`);
      return response.data;
    },
    enabled: !!tenantId,
  });
};

/**
 * Hook para Super Admin atualizar tema de um tenant específico
 */
export const useUpdateTenantTheme = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, ...data }: {
      tenantId: string;
      themeType: ImmersiveThemeType;
      config?: ThemeConfig;
      colorVariant?: string;
      reducedMotion?: boolean;
      highContrast?: boolean;
    }) => {
      const response = await api.put<ImmersiveThemeResponse>(`/theming/immersive/tenant/${tenantId}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['immersive-theme', 'tenant', variables.tenantId], data);
    },
  });
};
